/**
 * Zihinsel sentez: şeffaf katmanlar ve kâğıt katlama.
 *
 * İkisi de aynı işi istiyor — bir dönüşümü zihinde uygulayıp sonucunu
 * görmek — ama farklı dönüşümler: biri üst üste bindirme, öteki
 * aynalama. `mental_rotation` ile aynı aile; orada dönüşüm döndürme.
 *
 * İKİSİNDE DE SONUÇ ÖNCE, PARÇALAR SONRA. Katmanlarda önce birleşim
 * seçiliyor, sonra o birleşim katmanlara dağıtılıyor; katlamada önce
 * katlar ve delikler belirleniyor, açılmış hâl onlardan hesaplanıyor.
 * Ters yönde üretip "sonuç makul mü" diye bakmak, bu projede iki kez
 * çöken yaklaşım.
 */

import { Rng } from '../../prng'
import { gradeSingle } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, RenderNode } from '../types'

/** İki ızgara birebir aynı mı */
function ayniMi(a: boolean[], b: boolean[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i])
}

/* ================================================================== *
 * 45. Şeffaf katmanlar
 *
 * İki ya da üç şeffaf kâğıt üst üste konuyor; nihai görüntü
 * soruluyor.
 *
 * İŞLEM "VEYA", "DIŞLAYAN VEYA" DEĞİL. İstek listesinde ikisi de
 * geçiyordu. Üst üste konan iki şeffaf kâğıtta aynı yerde iki nokta
 * varsa sonuç yine noktadır — kâğıtlar birbirini silmez. XOR
 * koysaydık, sayfadaki resim bir soruyu, sorduğumuz soru başkasını
 * anlatırdı: çocuk kâğıtları hayal ederek doğru cevabı bulur, biz
 * onu yanlış sayardık. Zorluk katman sayısından ve ızgara
 * büyüklüğünden geliyor.
 *
 * ÇELDİRİCİLER BİRLEŞİMİN YAKININDAN. Rastgele bir desen çeldirici
 * değil: bakar bakmaz elenir ve seçenek sayısı kâğıt üstünde
 * görünenden az olur. Buradakiler ya bir katmanı atlıyor, ya bir
 * hücre fazla, ya bir hücre eksik.
 * ================================================================== */
export const overlayLayers: ExerciseTemplate = {
  code: 'overlay_layers',
  title: 'Üst üste koy',
  domains: ['working_memory_visual', 'selective_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Izgara kenarı */
      boyut: scale(l, 3, 5),
      /** Kaç şeffaf kâğıt */
      katman: l >= 6 ? 3 : 2,
      /** Kaç seçenek */
      secenek: scale(l, 3, 5),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['boyut'] as number
    const katmanSayisi = p['katman'] as number
    const secenekSayisi = p['secenek'] as number
    const hucre = n * n

    /*
     * BİRLEŞİM ÖNCE seçiliyor.
     *
     * Katmanlar rastgele doldurulup birleşimleri alınsaydı, birleşim
     * ızgaranın neredeyse tamamı çıkabilirdi: her hücresi dolu bir
     * cevap, çeldiricilerden "bir hücre eksik" olanı da ayırt
     * edilemez yapıyor ve görev bakmakla çözülüyor. Doluluk burada
     * üçte bir ile üçte iki arasında tutuluyor.
     */
    const dolulukAlt = Math.max(2, Math.round(hucre * 0.35))
    const dolulukUst = Math.max(dolulukAlt + 1, Math.round(hucre * 0.65))
    const birlesimSayisi = rng.int(dolulukAlt, dolulukUst)
    const birlesimHucreleri = rng.sample(
      Array.from({ length: hucre }, (_, i) => i),
      birlesimSayisi,
    )

    /*
     * Dağıtım: her hücre en az bir katmana, her katman en az bir
     * hücreye. Boş bir katman, sayfada hiçbir şey anlatmayan bir
     * kâğıt demek; çocuk onu çevirip durur.
     */
    const katmanlar: boolean[][] = Array.from({ length: katmanSayisi }, () =>
      new Array<boolean>(hucre).fill(false),
    )
    birlesimHucreleri.forEach((h, i) => {
      // İlk katmanlara sırayla birer hücre: hiçbiri boş kalmasın
      const temel = i < katmanSayisi ? i : rng.int(0, katmanSayisi - 1)
      katmanlar[temel]![h] = true
      // Bir kısmı iki katmanda birden: üst üste binme görünür olsun
      if (rng.bool(0.3)) {
        const ikinci = (temel + 1 + rng.int(0, katmanSayisi - 2)) % katmanSayisi
        katmanlar[ikinci]![h] = true
      }
    })

    const dogru = Array.from({ length: hucre }, (_, i) => katmanlar.some((k) => k[i]!))

    /* ---- Çeldiriciler ---- */
    const celdiriciler: boolean[][] = []
    const ekle = (c: boolean[]) => {
      if (ayniMi(c, dogru)) return
      if (celdiriciler.some((x) => ayniMi(x, c))) return
      if (celdiriciler.length < secenekSayisi - 1) celdiriciler.push(c)
    }

    // 1) Bir katmanı atlamış hâl
    for (let atla = 0; atla < katmanSayisi; atla++) {
      ekle(
        Array.from({ length: hucre }, (_, i) =>
          katmanlar.some((k, j) => j !== atla && k[i]!),
        ),
      )
    }
    // 2) Bir hücre eksik / bir hücre fazla
    for (const h of rng.shuffle(Array.from({ length: hucre }, (_, i) => i))) {
      const c = dogru.slice()
      c[h] = !c[h]
      ekle(c)
    }
    /*
     * İki hücre birden değişmiş hâller SON ÇARE.
     *
     * Küçük ızgarada tek hücrelik varyasyonlar seçenek sayısına
     * yetmeyebilir; yetmediğinde seçenek eksik basmak, sayfanın
     * zorluğunu düzeyden bağımsız hale getirir.
     */
    for (let a = 0; a < hucre && celdiriciler.length < secenekSayisi - 1; a++) {
      for (let b = a + 1; b < hucre && celdiriciler.length < secenekSayisi - 1; b++) {
        const c = dogru.slice()
        c[a] = !c[a]
        c[b] = !c[b]
        ekle(c)
      }
    }

    const sirali = rng.shuffle([
      { on: dogru, correct: true },
      ...celdiriciler.map((on) => ({ on, correct: false })),
    ])

    const render: RenderNode[] = [
      {
        t: 'tiles',
        n,
        items: katmanlar.map((on, i) => ({
          id: 'k' + i,
          on,
          caption: `${i + 1}. kâğıt`,
        })),
      },
      { t: 'text', value: 'Seçenekler:', size: 'sm' },
      {
        t: 'tiles',
        n,
        items: sirali.map((o, i) => ({ id: 'o' + i, on: o.on })),
        pick: true,
      },
    ]

    return {
      code: 'overlay_layers',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        `${katmanSayisi === 2 ? 'İki' : 'Üç'} şeffaf kâğıt üst üste konuyor. ` +
        'Kâğıtlar saydam: alttaki nokta üstteki kâğıdın altından görünüyor. ' +
        'Sonuç hangisi olur?',
      spokenPrompt:
        'Kâğıtlar şeffaf; üst üste koyduğunda hiçbir nokta kaybolmuyor, ' +
        'hepsi birden görünüyor.',
      render,
      solution: 'o' + sirali.findIndex((o) => o.correct),
      grading: 'auto',
      estimatedSeconds: 25 + katmanSayisi * 10,
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    return gradeSingle(instance.solution, answer)
  },
  anahtar(instance) {
    return `${Number(String(instance.solution).slice(1)) + 1}. seçenek`
  },
}

/* ================================================================== *
 * 46. Kâğıt katlama
 *
 * Kare kâğıt birkaç kez katlanıyor, katlanmış hâlde deliniyor,
 * açıldığında deliklerin nerede olduğu soruluyor.
 *
 * KATLANMIŞ KÂĞIT KARE DEĞİL ve kare çizilmiyor. İki kez katlanan bir
 * kare, dörtte bir boyunda bir dikdörtgen; kare çizilseydi çocuk
 * deliği kâğıdın yanlış yerinde görürdü ve aynalama hesabı baştan
 * yanlış çıkardı. `tiles` düğümü bu yüzden satır sayısını ayrı
 * alıyor.
 *
 * AÇMA İŞLEMİ AYNALAMA. Her katın tersi, etkin alanın içeriğini
 * öbür yarıya aynalayıp alanı iki katına çıkarmak. Delikler
 * katlanmış hâlde deliniyor, yani her kat deliği ikiye katlıyor.
 * ================================================================== */

type Kat = 'sag' | 'sol' | 'yukari' | 'asagi'

const KAT_METNI: Record<Kat, string> = {
  sag: 'sağdan sola',
  sol: 'soldan sağa',
  yukari: 'yukarıdan aşağıya',
  asagi: 'aşağıdan yukarıya',
}

interface Alan {
  x: number
  y: number
  g: number
  y2: number
}

export const paperFold: ExerciseTemplate = {
  code: 'paper_fold',
  title: 'Katla ve del',
  domains: ['working_memory_visual', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /**
       * Izgara kenarı kat sayısıyla BİRLİKTE büyüyor.
       *
       * Her kat deliği ikiye katlıyor: 4×4'lük bir kâğıtta üç kat,
       * tek delikten sekiz delik çıkarıyor — on altı hücrenin
       * yarısı. O yoğunlukta sayfa okunmuyor ve "bir hücre farklı"
       * çeldiricileri gözle ayırt edilemiyor; sayfaya bakınca
       * görüldü. Üç kat 8×8'de altmış dörtte sekiz delik demek,
       * yani rahat okunan bir sayfa.
       *
       * Kenar İKİNİN KUVVETİ olmak zorunda: katlamak yarıya bölmek
       * ve 6 ikiye bölündükten sonra bir daha bölünmüyor.
       */
      boyut: l >= 7 ? 8 : 4,
      /** Kaç kez katlandı */
      kat: l >= 7 ? 3 : scale(l, 1, 2),
      /** Kaç delik */
      delik: l >= 9 ? 2 : 1,
      secenek: scale(l, 3, 5),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['boyut'] as number
    const katSayisi = p['kat'] as number
    const delikSayisi = p['delik'] as number
    const secenekSayisi = p['secenek'] as number

    /*
     * Katları seç.
     *
     * İKİ KURAL, ikisi de sayfaya bakınca kondu:
     *
     * 1. Her zaman UZUN kenar katlanıyor. Rastgele bırakılmıştı ve
     *    8×8 bir kâğıt üç kez yatay katlanıp 1×8'lik bir şeride
     *    dönüşebiliyordu. Matematiksel olarak doğru, kâğıt olarak
     *    saçma ve çocuğun elinde canlandıramayacağı bir şey.
     *
     * 2. Bir eksen hangi yöne katlandıysa hep o yöne katlanıyor.
     *    "Sağdan sola, sonra soldan sağa" cümlesi doğruydu — ikinci
     *    kat, kalan yarının kendi içinde — ama yönergeyi okuyan
     *    çocuk için anlamsız. Yön tutarlı olunca cümle "sağdan sola,
     *    sonra yine sağdan sola" oluyor ve elde yapılabiliyor.
     */
    const katlar: Kat[] = []
    let alan: Alan = { x: 0, y: 0, g: n, y2: n }
    const yatayYon: Kat = rng.bool() ? 'sag' : 'sol'
    const dikeyYon: Kat = rng.bool() ? 'yukari' : 'asagi'
    for (let i = 0; i < katSayisi; i++) {
      const yatayOlur = alan.g >= 2
      const dikeyOlur = alan.y2 >= 2
      if (!yatayOlur && !dikeyOlur) break
      const yatay = !dikeyOlur || (yatayOlur && (alan.g > alan.y2 || (alan.g === alan.y2 && rng.bool())))
      if (yatay) {
        katlar.push(yatayYon)
        if (yatayYon === 'sol') alan = { ...alan, x: alan.x + alan.g / 2, g: alan.g / 2 }
        else alan = { ...alan, g: alan.g / 2 }
      } else {
        katlar.push(dikeyYon)
        if (dikeyYon === 'asagi') alan = { ...alan, y: alan.y + alan.y2 / 2, y2: alan.y2 / 2 }
        else alan = { ...alan, y2: alan.y2 / 2 }
      }
    }

    /* Delikler katlanmış alanın içinde */
    const kapasite = alan.g * alan.y2
    const sayi = Math.min(delikSayisi, kapasite)
    const delikYerleri = rng.sample(
      Array.from({ length: kapasite }, (_, i) => i),
      sayi,
    )
    const katlanmis = new Array<boolean>(kapasite).fill(false)
    for (const d of delikYerleri) katlanmis[d] = true

    /**
     * Açma: katları ters sırada geri al.
     *
     * Her adımda etkin alanın içeriği öbür yarıya aynalanıyor ve alan
     * iki katına çıkıyor. Tam ızgara üzerinde çalışıyor çünkü alanın
     * kâğıt içindeki YERİ de değişiyor ("soldan sağa" katlandıysa
     * etkin alan sağ yarı).
     */
    const ac = (atlanan = -1, oteleme = false): boolean[] => {
      const tam = new Array<boolean>(n * n).fill(false)
      let a = alan
      for (let i = 0; i < kapasite; i++) {
        if (!katlanmis[i]!) continue
        tam[(a.y + Math.floor(i / a.g)) * n + (a.x + (i % a.g))] = true
      }
      for (let i = katlar.length - 1; i >= 0; i--) {
        const k = katlar[i]!
        const yatay = k === 'sag' || k === 'sol'
        const yeni: Alan = yatay
          ? { ...a, x: k === 'sol' ? a.x - a.g : a.x, g: a.g * 2 }
          : { ...a, y: k === 'asagi' ? a.y - a.y2 : a.y, y2: a.y2 * 2 }
        if (i !== atlanan) {
          // Açılan öbür yarının sol üst köşesi
          const hx0 = yatay ? (k === 'sol' ? yeni.x : a.x + a.g) : a.x
          const hy0 = yatay ? a.y : k === 'asagi' ? yeni.y : a.y + a.y2
          for (let dy = 0; dy < a.y2; dy++) {
            for (let dx = 0; dx < a.g; dx++) {
              if (!tam[(a.y + dy) * n + (a.x + dx)]) continue
              /*
               * Aynalama: kat çizgisinin öbür yanında TERS konum.
               * `oteleme` bunu kasten bozuyor — deliği aynalamak
               * yerine olduğu gibi kaydırıyor. Çocukların en sık
               * yaptığı hata bu ve çeldirici olarak tam da bu
               * gerekiyor; rastgele bir desen bakar bakmaz elenir.
               */
              const ox = oteleme ? dx : yatay ? a.g - 1 - dx : dx
              const oy = oteleme ? dy : yatay ? dy : a.y2 - 1 - dy
              tam[(hy0 + oy) * n + (hx0 + ox)] = true
            }
          }
        }
        a = yeni
      }
      return tam
    }

    const dogru = ac()

    /* ---- Çeldiriciler ---- */
    const celdiriciler: boolean[][] = []
    const ekle = (c: boolean[]) => {
      if (ayniMi(c, dogru)) return
      if (!c.some(Boolean)) return
      if (celdiriciler.some((x) => ayniMi(x, c))) return
      if (celdiriciler.length < secenekSayisi - 1) celdiriciler.push(c)
    }

    // 1) Bir katlamayı açmayı unutmuş hâl
    for (let i = 0; i < katlar.length; i++) ekle(ac(i))
    // 2) Aynalamak yerine ötelemiş hâl — en sık yapılan hata
    ekle(ac(-1, true))
    // 3) Doğru cevabın bir deliği eksik / fazla
    for (const h of rng.shuffle(Array.from({ length: n * n }, (_, i) => i))) {
      const c = dogru.slice()
      c[h] = !c[h]
      ekle(c)
    }

    const sirali = rng.shuffle([
      { on: dogru, correct: true },
      ...celdiriciler.map((on) => ({ on, correct: false })),
    ])

    /*
     * Aynı yön art arda geliyorsa ikincisinde "yine" deniyor:
     * "sağdan sola, sonra yine sağdan sola". Aynı cümleyi iki kez
     * okuyan çocuk, yanlış okuduğunu sanıp başa dönüyordu.
     */
    const katMetni = katlar
      .map((k, i) => (i > 0 && katlar[i - 1] === k ? `yine ${KAT_METNI[k]}` : KAT_METNI[k]))
      .join(', sonra ')

    return {
      code: 'paper_fold',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        `Kare bir kâğıt ${katMetni} katlandı. Katlanmış hâlde aşağıdaki ` +
        `${sayi === 1 ? 'delik' : 'delikler'} açıldı. Kâğıt açıldığında hangisi olur?`,
      spokenPrompt:
        'Kâğıt katlıyken delindi, yani delik kâğıdın üst üste gelen bütün ' +
        'katlarını birden deldi. Açınca her delik aynalanmış olarak çıkar.',
      render: [
        {
          t: 'tiles',
          n: alan.g,
          satir: alan.y2,
          items: [{ id: 'katli', on: katlanmis, caption: 'katlanmış kâğıt' }],
        },
        { t: 'text', value: 'Seçenekler (açılmış kâğıt):', size: 'sm' },
        {
          t: 'tiles',
          n,
          items: sirali.map((o, i) => ({ id: 'o' + i, on: o.on })),
          pick: true,
        },
      ],
      solution: 'o' + sirali.findIndex((o) => o.correct),
      grading: 'auto',
      estimatedSeconds: 30 + katSayisi * 10,
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    return gradeSingle(instance.solution, answer)
  },
  anahtar(instance) {
    return `${Number(String(instance.solution).slice(1)) + 1}. seçenek`
  },
}
